import { useState } from "react";
import { CircularProgress, Fab, Popover, Stack, Tooltip, Typography } from "@mui/material";
import { ManageHistory } from "@mui/icons-material";
import { formatDataHoraBR, historicoDaGerada, type EventoDaGerada } from "@/api/escala.service";
import type { User } from "@/api/user.service";
import { useAuth } from "@/context/AuthContext";
import { botaoFlutuante } from "@/components/escala/espelhoEstilos";

const ROTULO_EVENTO: Record<EventoDaGerada["evento"], string> = {
  CRIADA: "Criada",
  VALIDADA: "Validada",
  DESVALIDADA: "Voltou a rascunho",
  APROVADA: "Aprovada",
  DESAPROVADA: "Desaprovada",
  CANCELADA: "Cancelada",
  REMOVIDA: "Removida",
};

// O nome pela lista de usuários; sem ele, o que a trilha guardou.
function autor(evento: EventoDaGerada, usuarios: User[]) {
  return usuarios.find((u) => u.id === evento.user_id)?.name || evento.usuario || "";
}

interface HistoricoDaGeradaProps {
  geradaId: string;
  usuarios: User[];
}

// Botão do canto inferior direito do espelho: abre, acima dele, as mudanças de estado da escala, com
// quem e quando. Busca a cada clique, porque o estado muda com a tela aberta.
export function HistoricoDaGerada({ geradaId, usuarios }: HistoricoDaGeradaProps) {
  const { user } = useAuth();
  const clientId = user!.loggedClient!.id;
  const [ancora, setAncora] = useState<HTMLElement | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [eventos, setEventos] = useState<EventoDaGerada[]>([]);
  const [erro, setErro] = useState(false);

  // Abre só com a lista pronta: o popover se posiciona pela altura do conteúdo, e crescer depois de
  // aberto faz ele pular.
  const abrir = (el: HTMLElement) => {
    if (carregando) return;
    setCarregando(true);
    historicoDaGerada(geradaId, clientId)
      .then((r) => {
        setEventos(r.eventos ?? []);
        setErro(false);
      })
      .catch(() => setErro(true))
      .finally(() => {
        setCarregando(false);
        setAncora(el);
      });
  };

  return (
    <>
      <Tooltip title="Auditoria" placement="left">
        <Fab
          size="small"
          aria-label="Auditoria"
          aria-expanded={!!ancora}
          aria-busy={carregando}
          sx={botaoFlutuante}
          onClick={(e) => abrir(e.currentTarget)}
        >
          {carregando ? <CircularProgress size={18} aria-label="Carregando a auditoria" /> : <ManageHistory fontSize="small" />}
        </Fab>
      </Tooltip>
      <Popover
        open={!!ancora}
        anchorEl={ancora}
        onClose={() => setAncora(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        slotProps={{ paper: { sx: { mt: -1, p: 2, minWidth: 320, maxWidth: 440, maxHeight: "70vh", overflowY: "auto" } } }}
      >
        <Stack spacing={1.25} aria-label="Auditoria">
          <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            Auditoria
          </Typography>
          {erro ? (
            <Typography variant="body2" color="error">
              Não foi possível carregar a auditoria.
            </Typography>
          ) : eventos.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nenhuma mudança de estado registrada.
            </Typography>
          ) : (
            eventos.map((e, i) => {
              const quem = autor(e, usuarios);
              return (
                <Stack key={`${e.evento}-${e.em}-${i}`} direction="row" spacing={2} justifyContent="space-between" alignItems="baseline">
                  <Typography variant="body2" fontWeight={600}>
                    {ROTULO_EVENTO[e.evento] ?? e.evento}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: "right" }}>
                    {quem ? `${formatDataHoraBR(e.em)} — ${quem}` : formatDataHoraBR(e.em)}
                  </Typography>
                </Stack>
              );
            })
          )}
        </Stack>
      </Popover>
    </>
  );
}
